import React from 'react';
import styled from 'styled-components';
import { colors } from '../theme/ColorTheme';
import { borders } from '../theme/Borders';
import mediaQ from '../theme/responsive';
import ReactHtmlParser from 'react-html-parser';
import InfoBox from './InfoBox';

export default ({
    heading,
    icon,
    secondLine,
    firstLineToolTip,
    secondLineToolTip,
    limitFirstLine,
    limitSecondLine,
    value,
    secondLineValue,
    scaleMinValue,
    scaleMaxValue,
    hasInfoBox,
    infoBoxContent, 
    infoBoxHeadline, 
    infoBoxPositioning 
}) => {
    const startScale = 10;
    const endScale = 50;
    const scale = [];
    for (let i = startScale; i <= endScale; i += 5) {
        scale.push(i);
    }
    return (
        <Schallberechnung>
            <Heading>
                <span>{heading}</span>
                {hasInfoBox ?  <InfoBox positioning={infoBoxPositioning} headline={infoBoxHeadline} content={infoBoxContent}  />    
         : null}
            </Heading>
            <Content>
                <StyledIcon src={icon} />
                <Result>
                    <FirstColorLineLimit scaleMinValue={scale[0]} scaleMaxValue={scale.slice(-1)[0]} secondLine={secondLine} limitFirstLine={limitFirstLine} />
                    <ResultLineBg>
                        <FirstColorLine
                            value={value}
                            firstLineToolTip={firstLineToolTip}
                            scaleMinValue={scale[0]}
                            scaleMaxValue={scale.slice(-1)[0]}
                            secondLine={secondLine}
                            limitFirstLine={limitFirstLine}>
                            <ToolTip>
                                <ToolTipText>
                                    {value} {firstLineToolTip}
                                </ToolTipText>
                                <Arrow />
                            </ToolTip>
                        </FirstColorLine>
                        {secondLine ? (
                            <React.Fragment>
                                <SecondColorLineLimit scaleMinValue={scale[0]} scaleMaxValue={scale.slice(-1)[0]} limitSecondLine={limitSecondLine} />
                                <SecondColorLine
                                    secondLineValue={secondLineValue}
                                    secondLineToolTip={secondLineToolTip}
                                    scaleMinValue={scale[0]}
                                    scaleMaxValue={scale.slice(-1)[0]}
                                    secondLine={secondLine}
                                    limitSecondLine={limitSecondLine}>
                                    <ToolTip>
                                        <ToolTipText>
                                            {secondLineValue} {secondLineToolTip}
                                        </ToolTipText>
                                        <Arrow />
                                    </ToolTip>
                                </SecondColorLine>
                            </React.Fragment>
                        ) : (
                            false
                        )}
                    </ResultLineBg>
                    <ResultNumbers>
                        {scale && (
                            <React.Fragment>
                                {scale.map(scale => (
                                    <span key={scale}>{scale}</span>
                                ))}
                            </React.Fragment>
                        )}
                    </ResultNumbers>
                </Result>
            </Content>
        </Schallberechnung>
    );
};

const Heading = styled.div`
    display: flex;
`;

const Arrow = styled.i`
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -12px;
    width: 24px;
    height: 12px;
    overflow: hidden;
    &::after {
        content: '';
        position: absolute;
        width: 12px;
        height: 12px;
        left: 50%;
        transform: translate(-50%, -50%) rotate(45deg);
        background-color: ${colors.white};
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
    }
`;

const ToolTipText = styled.p`
    text-align: center;
    vertical-align: middle;
`;

const ToolTip = styled.div`
    color: ${colors.black};
    background-color: ${colors.white};
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
    display: none;
    top: -10px;
    left: 50%;
    transform: translate(-50%, -100%);
    padding: 5px 5px;
    font-size: 0.8667rem;
    position: absolute;
    min-width: 230px;
    z-index: 99;
    ${mediaQ.phone`
        min-width: 130px;
        font-size: 80%;
        padding: 2px 5px;
    `}
`;

const Schallberechnung = styled.div`
    line-height:20px;
    padding: 10px;
    width: 45%;
    ${mediaQ.tablet`
        width: 50%;
    `}
    ${mediaQ.phone`
        width: 100%;
    `}
`;

const Content = styled.div`
    display: flex;
    align-items: center;
    margin-top: 40px;
    ${mediaQ.tablet`
        justify-content: center;
        margin-top: 20px;
    `}
`;

const StyledIcon = styled.img`
    object-fit: scale-down;
    height: auto;
    margin-right: 2rem;
    max-width: 3rem;
    ${mediaQ.phone`
        margin-right: 0;
    `}
    ${mediaQ.smallPhone`
        margin-bottom: 20px;
    `}
`;

const Result = styled.div`
    width: 80%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const ResultLineBg = styled.span`
    height: 20px;
    position: relative;
    background-color: ${colors.resultColorLineBg};
    width: 100%;
`;

const FirstColorLine = styled.span`
    width: ${props => ((props.value - props.scaleMinValue) / (props.scaleMaxValue - props.scaleMinValue)) * 100}%;
    max-width: 100%;
    position: absolute;
    background-color: ${props => (props.limitFirstLine < props.value ? colors.projectOrange : colors.projectDarkBlue)};
    height: ${props => (props.secondLine ? '10px' : '20px')};
    cursor: pointer;
    transition: all 0.3s;
    &:hover {
        ${ToolTip} {
            display: block;
        }
    }
`;

const SecondColorLine = styled.span`
    width: ${props => ((props.secondLineValue - props.scaleMinValue) / (props.scaleMaxValue - props.scaleMinValue)) * 100}%;
    max-width: 100%;
    position: absolute;
    background-color: ${props => (props.limitSecondLine < props.secondLineValue ? colors.projectOrange : colors.projectLightBlue)};
    height: 10px;
    margin-top: 10px;
    ${borders.borderTop};
    border-color: ${colors.white};
    cursor: pointer;
    transition: all 0.3s;
    &:hover {
        ${ToolTip} {
            display: block;
        }
    }
`;

const ResultNumbers = styled.div`
    margin-top: 18px;
    font-size: 0.8rem;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    span {
        color: ${colors.borderColor};
    }
    ${mediaQ.phone`
        span {
            word-spacing: 9px;
        }
    `}
`;

const SecondColorLineLimit = styled.div`
    width: 100%;
    position: relative;
    ::before {
        content: '';
        margin-left: ${props => ((props.limitSecondLine - props.scaleMinValue) / (props.scaleMaxValue - props.scaleMinValue)) * 100}%;
        position: absolute;
        ${borders.borderRightDashed} ${props => (props.limitSecondLine < props.secondLineValue ? colors.projectOrange : colors.projectLightBlue)};
        height: 46px;
        bottom: -33px;
        z-index: 1;

        opacity: ${props => (props.limitSecondLine ? '1' : '0')};
    }
`;

const FirstColorLineLimit = styled.div`
    width: 100%;
    position: relative;
    ::before {
        content: '';
        margin-left: ${props => ((props.limitFirstLine - props.scaleMinValue) / (props.scaleMaxValue - props.scaleMinValue)) * 100}%;
        position: absolute;
        ${borders.borderRight} ${props => (props.limitFirstLine < props.value ? colors.projectOrange : colors.projectDarkBlue)};
        height: 46px;
        bottom: -33px;
        z-index: 1;

        opacity: ${props => (props.limitFirstLine ? '1' : '0')};
    }
`;
